/**
 * A transição da área de membros (docs/PLANO_MEMBROS_E_FICHA.md, seção 14).
 *
 * View Transitions API: o navegador fotografa o antes, o `flushSync` aplica a
 * mudança AGORA (o snapshot "depois" é tirado assim que o callback retorna —
 * deixar para o próximo ciclo do React seria tarde demais), e ele anima as
 * duas fotos. A região animada é a que o SidePanel batiza de `painel-membros`;
 * o CSS que escolhe o movimento mora no GameSession.css e lê a direção deste
 * data-attribute na raiz, porque os pseudo-elementos ::view-transition-*
 * pendem do <html>.
 *
 * Direções:
 * - 'abre'   — a aba de Membros entrando no lugar de outra (lista desliza da
 *              esquerda; o resto da tela faz o cross-fade padrão)
 * - 'fecha'  — a aba de Membros saindo (lista desliza de volta para a esquerda)
 * - 'avanca' — lista -> ficha, dentro da aba
 * - 'volta'  — ficha -> lista, dentro da aba
 *
 * Sem a API (aparelho antigo, jsdom), a mudança é aplicada direto e vale o
 * fallback de cada tela (a animação de entrada do tw-animate). Quem chama
 * decide QUANDO animar (só no celular / só no modo empilhado); aqui só mora o
 * COMO.
 */

import { flushSync } from 'react-dom'

export type DirecaoDeMembros = 'abre' | 'fecha' | 'avanca' | 'volta'

export function transicaoDeMembros(direcao: DirecaoDeMembros, mudanca: () => void) {
  const doc = document as Document & {
    startViewTransition?: (atualiza: () => void) => { finished: Promise<void> }
  }
  if (!doc.startViewTransition) {
    mudanca()
    return
  }
  document.documentElement.dataset.membrosTransicao = direcao
  doc.startViewTransition(() => {
    flushSync(mudanca)
  }).finished.finally(() => {
    delete document.documentElement.dataset.membrosTransicao
  })
}

/** O navegador desta sessão sabe fazer a transição nativa? Quem tem a API não
 *  precisa (nem deve) somar a animação de entrada do fallback — o movimento
 *  dobraria. */
export const temTransicaoNativa = () =>
  typeof document !== 'undefined' && 'startViewTransition' in document
